import { CrmLead, CrmTask, dateTimeBR, isSameDay, PRIORITIES, TASK_TYPES } from "@/lib/crm";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, CalendarDays } from "lucide-react";
import { useState } from "react";

interface Props {
  leads: CrmLead[];
  tasks: CrmTask[];
  onOpenLead: (lead: CrmLead) => void;
  onToggleTask: (task: CrmTask) => void;
}

type View = "dia" | "semana" | "mes";

interface AgendaItem {
  id: string;
  when: string;
  title: string;
  kind: string;
  lead?: CrmLead;
  done?: boolean;
  task?: CrmTask;
}

const CrmAgenda = ({ leads, tasks, onOpenLead, onToggleTask }: Props) => {
  const [view, setView] = useState<View>("semana");

  const now = new Date();
  const limit = new Date(now);
  if (view === "dia") limit.setDate(now.getDate() + 1);
  if (view === "semana") limit.setDate(now.getDate() + 7);
  if (view === "mes") limit.setDate(now.getDate() + 30);

  const leadById = (id: string) => leads.find((l) => l.id === id);

  const items: AgendaItem[] = [
    ...tasks.map((t) => ({
      id: `t-${t.id}`,
      when: t.due_at,
      title: t.title,
      kind: TASK_TYPES.find((x) => x.value === t.task_type)?.label || "Tarefa",
      lead: leadById(t.lead_id),
      done: t.is_done,
      task: t,
    })),
    ...leads
      .filter((l) => l.visit_date)
      .map((l) => ({
        id: `v-${l.id}`,
        when: l.visit_date as string,
        title: `Visita — ${l.property_name || l.neighborhood || "imóvel"}`,
        kind: "Visita",
        lead: l,
      })),
    ...leads
      .filter((l) => l.follow_up_at && !l.follow_up_done)
      .map((l) => ({
        id: `f-${l.id}`,
        when: l.follow_up_at as string,
        title: l.follow_up_notes || "Follow up",
        kind: "Follow up",
        lead: l,
      })),
  ]
    .filter((i) => {
      const d = new Date(i.when);
      return d <= limit;
    })
    .sort((a, b) => new Date(a.when).getTime() - new Date(b.when).getTime());

  const groups = items.reduce<Record<string, AgendaItem[]>>((acc, i) => {
    const key = new Date(i.when).toLocaleDateString("pt-BR", {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
    });
    (acc[key] ||= []).push(i);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-accent" />
        <h3 className="font-display text-base font-bold text-foreground">Agenda</h3>
        <div className="ml-auto flex gap-1">
          {(["dia", "semana", "mes"] as View[]).map((v) => (
            <Button
              key={v}
              size="sm"
              variant={view === v ? "gold" : "outline"}
              onClick={() => setView(v)}
              className="font-body capitalize"
            >
              {v === "mes" ? "Mês" : v}
            </Button>
          ))}
        </div>
      </div>

      {Object.keys(groups).length === 0 ? (
        <Card className="p-6 text-center font-body text-sm text-muted-foreground">
          Nenhum compromisso neste período.
        </Card>
      ) : (
        Object.entries(groups).map(([day, list]) => (
          <Card key={day} className="p-4 border border-border">
            <p className="font-display text-sm font-bold text-foreground capitalize mb-3">
              {day}
              {isSameDay(list[0].when) && (
                <Badge className="ml-2 bg-accent text-accent-foreground font-body text-[10px]">hoje</Badge>
              )}
            </p>
            <ul className="space-y-2">
              {list.map((i) => {
                const late = new Date(i.when) < now && !i.done;
                return (
                  <li
                    key={i.id}
                    className={`flex items-center gap-3 rounded-md border p-2 ${
                      late ? "border-destructive/50 bg-destructive/5" : "border-border"
                    } ${i.done ? "opacity-50" : ""}`}
                  >
                    <span className="font-body text-xs text-muted-foreground w-24 shrink-0">
                      {dateTimeBR(i.when).split(" ")[1]}
                    </span>
                    <Badge variant="outline" className="font-body text-[10px] shrink-0">
                      {i.kind}
                    </Badge>
                    <span className={`font-body text-sm truncate ${i.done ? "line-through" : ""}`}>
                      {i.title}
                    </span>
                    {i.lead && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onOpenLead(i.lead as CrmLead)}
                        className="ml-auto font-body text-xs shrink-0"
                      >
                        {i.lead.client_name}
                      </Button>
                    )}
                    {i.task && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onToggleTask(i.task as CrmTask)}
                        className="shrink-0"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        ))
      )}
    </div>
  );
};

export default CrmAgenda;
